import { useCallback, useEffect, useReducer, useRef } from 'react';

import {
    GenerationsOccurrenceList,
    type GenerationsRecurrenceSelectionPreview,
    loadGenerationsRecurrenceSelection,
} from '@apps/web/components/outreaches/generations_recurrence_preview';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '@apps/web/components/ui/alert';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Spinner } from '@apps/web/components/ui/spinner';
import { getApiClient } from '@apps/web/lib/api_client';
import { ApiError } from '@libraries/typescript/api_client';

/**
 * One shift's unambiguous caregiver-specific Generations payroll option.
 * `previewDigest` binds the item and rate to the vendor state the preview
 * read; the fill request echoes it back so the writer can refuse a stale
 * approval. The rate is kept as the exact string Generations rendered
 * (four decimals) and is never reformatted or edited.
 */
export type GenerationsPayrollPreview = {
    readonly payrollItemName: string;
    readonly payrollRate: string;
    readonly previewDigest: string;
    // Present only when this visit is confirmed and the agency protects
    // confirmed visits. Generations itself posts silently for an
    // administrator; Phoebe shows `confirmedScheduleWarning` and echoes this
    // back, so the writer can tell an answered visit from an unanswered one.
    readonly confirmedScheduleAcknowledgement: string | null;
    readonly confirmedScheduleWarning: string | null;
};

/**
 * One of several active payroll items Generations offers for a caregiver
 * when the vendor leaves the field blank. Choosing one re-runs the preview
 * with it, which is what turns the choice into a confirmed
 * `GenerationsPayrollPreview` with a digest.
 */
export type GenerationsPayrollOption = {
    readonly payrollItemName: string;
    readonly payrollRate: string;
};

const GENERATIONS_PREVIEW_FALLBACK_MESSAGE =
    'Generations could not confirm a pay rate.';
const GENERATIONS_ASSIGNMENT_CONFLICT_CODE =
    'generations_recurrence_conflicting_assignment';
export const GENERATIONS_PORTAL_BUSY_CODE = 'generations_portal_busy';

export const GENERATIONS_PREVIEW_GUIDANCE_MESSAGE =
    'Check the visit in Generations. Close and reopen to try writeback again, or continue with a Phoebe-only assignment.';

/**
 * Generations gave an agency one portal login and a schedule refresh can own
 * it for minutes, so the coordinator is not being told to go fix anything —
 * nobody looked a pay rate up yet. Sending them into Generations to check a
 * visit that is fine would waste the trip.
 */
export const GENERATIONS_PORTAL_BUSY_GUIDANCE_MESSAGE =
    'Close and reopen this window to try again, or continue with a Phoebe-only assignment.';

/**
 * Prefer the server's safe structured message: every refusal the preview
 * route spells out is either a settled state (409) or the one transient
 * state worth retrying (503), both with copy written for coordinators. Any
 * other failure (network, unclassified 5xx, lost outcome) gets a plain
 * refusal rather than a guess about what happened upstream.
 */
type GenerationsPreviewError = {
    readonly code: string | null;
    readonly message: string;
};

function getGenerationsPreviewError(error: unknown): GenerationsPreviewError {
    if (
        error instanceof ApiError &&
        (error.status === 409 || error.status === 503)
    ) {
        const body: unknown = error.body;
        if (
            body !== null &&
            typeof body === 'object' &&
            'detail' in body &&
            body.detail !== null &&
            typeof body.detail === 'object' &&
            'message' in body.detail &&
            typeof body.detail.message === 'string' &&
            body.detail.message.length > 0
        ) {
            return {
                code:
                    'code' in body.detail &&
                    typeof body.detail.code === 'string'
                        ? body.detail.code
                        : null,
                message: body.detail.message,
            };
        }
    }
    return { code: null, message: GENERATIONS_PREVIEW_FALLBACK_MESSAGE };
}

type UseGenerationsPayrollPreviewsArgs = {
    // False outside an open live-mode Generations confirmation; flipping it
    // (or changing any identity below) discards previews and confirmation.
    readonly enabled: boolean;
    readonly organizationId: string | null;
    readonly outreachId: string;
    readonly caregiverId: string;
    // Canonical key of the selected shift set. The human confirmation
    // approves one exact set, so any change to the key voids it.
    readonly selectionKey: string;
};

type UseGenerationsPayrollPreviewsResult = {
    readonly previewsByShiftId: ReadonlyMap<string, GenerationsPayrollPreview>;
    // Visits whose caregiver has several active pay rates and no vendor
    // selection; each needs a choice before it has a preview.
    readonly optionsByShiftId: ReadonlyMap<
        string,
        ReadonlyArray<GenerationsPayrollOption>
    >;
    readonly isLoading: boolean;
    readonly loadError: string | null;
    readonly loadErrorCode: string | null;
    readonly isConfirmed: boolean;
    readonly recurrenceSelection: GenerationsRecurrenceSelectionPreview | null;
    readonly idempotentShiftIds: ReadonlySet<string>;
    readonly setConfirmed: (confirmed: boolean) => void;
    readonly resetPreviews: () => void;
    readonly loadPreviews: (shiftIds: ReadonlyArray<string>) => Promise<void>;
    readonly chooseOption: (
        shiftId: string,
        option: GenerationsPayrollOption,
    ) => Promise<void>;
};

type GenerationsPreviewData = {
    readonly previewsByShiftId: ReadonlyMap<string, GenerationsPayrollPreview>;
    readonly optionsByShiftId: ReadonlyMap<
        string,
        ReadonlyArray<GenerationsPayrollOption>
    >;
    readonly recurrenceSelection: GenerationsRecurrenceSelectionPreview | null;
    readonly idempotentShiftIds: ReadonlySet<string>;
    readonly isConfirmed: boolean;
};

type GenerationsPreviewState =
    | {
          readonly phase: 'idle';
          readonly data: GenerationsPreviewData;
      }
    | {
          readonly phase: 'loading';
          readonly data: GenerationsPreviewData;
      }
    | {
          readonly phase: 'mismatch';
          readonly data: GenerationsPreviewData;
      }
    | {
          readonly phase: 'ready';
          readonly data: GenerationsPreviewData;
      }
    | {
          readonly phase: 'error';
          readonly data: GenerationsPreviewData;
          readonly error: GenerationsPreviewError;
      };

type GenerationsPreviewAction =
    | { readonly type: 'invalidate'; readonly requestIsDraining: boolean }
    | { readonly type: 'start' }
    | {
          readonly type: 'recurrenceLoaded';
          readonly selection: GenerationsRecurrenceSelectionPreview;
      }
    | {
          readonly type: 'payrollLoaded';
          readonly shiftId: string;
          readonly preview: GenerationsPayrollPreview;
      }
    | {
          readonly type: 'optionsLoaded';
          readonly shiftId: string;
          readonly options: ReadonlyArray<GenerationsPayrollOption>;
      }
    | { readonly type: 'mismatch' }
    | { readonly type: 'complete'; readonly confirmed: boolean }
    | { readonly type: 'error'; readonly error: GenerationsPreviewError }
    | { readonly type: 'confirm'; readonly confirmed: boolean }
    | { readonly type: 'drained' };

function assertNever(value: never): never {
    throw new Error(`Unhandled Generations preview action: ${String(value)}`);
}

const EMPTY_PREVIEW_DATA: GenerationsPreviewData = {
    previewsByShiftId: new Map(),
    optionsByShiftId: new Map(),
    recurrenceSelection: null,
    idempotentShiftIds: new Set(),
    isConfirmed: false,
};

function generationsPreviewReducer(
    state: GenerationsPreviewState,
    action: GenerationsPreviewAction,
): GenerationsPreviewState {
    switch (action.type) {
        case 'invalidate':
            return {
                phase: action.requestIsDraining ? 'loading' : 'idle',
                data: EMPTY_PREVIEW_DATA,
            };
        case 'start':
            return { phase: 'loading', data: state.data };
        case 'recurrenceLoaded':
            return {
                phase: 'loading',
                data: {
                    ...state.data,
                    recurrenceSelection: action.selection,
                    idempotentShiftIds: new Set(
                        action.selection.idempotent_shift_ids,
                    ),
                },
            };
        case 'payrollLoaded': {
            // A confirmed preview settles the visit, so any options it was
            // still choosing among are done with.
            const optionsByShiftId = new Map(state.data.optionsByShiftId);
            optionsByShiftId.delete(action.shiftId);
            return {
                phase: 'loading',
                data: {
                    ...state.data,
                    previewsByShiftId: new Map(
                        state.data.previewsByShiftId,
                    ).set(action.shiftId, action.preview),
                    optionsByShiftId,
                },
            };
        }
        case 'optionsLoaded':
            return {
                phase: 'loading',
                data: {
                    ...state.data,
                    optionsByShiftId: new Map(state.data.optionsByShiftId).set(
                        action.shiftId,
                        action.options,
                    ),
                },
            };
        case 'mismatch':
            return { phase: 'mismatch', data: state.data };
        case 'complete':
            return {
                phase: 'ready',
                data: { ...state.data, isConfirmed: action.confirmed },
            };
        case 'error':
            return { phase: 'error', data: state.data, error: action.error };
        case 'confirm':
            return state.phase === 'ready'
                ? {
                      phase: 'ready',
                      data: { ...state.data, isConfirmed: action.confirmed },
                  }
                : state;
        case 'drained':
            return { phase: 'idle', data: EMPTY_PREVIEW_DATA };
        default:
            return assertNever(action);
    }
}

type ActivePreviewRun = {
    readonly isValid: () => boolean;
    readonly invalidate: () => void;
};

function createActivePreviewRun(): ActivePreviewRun {
    let invalidated = false;
    return {
        isValid: () => !invalidated,
        invalidate: () => {
            invalidated = true;
        },
    };
}

/**
 * Per-shift Generations payroll previews for the Assign & Fill confirmation.
 *
 * The preview route performs a real (certified non-persisting) vendor
 * postback, so requests happen only from an explicit load action. Payroll
 * requests stay serial so the first refusal stops before any later vendor
 * postback starts.
 */
export function useGenerationsPayrollPreviews({
    enabled,
    organizationId,
    outreachId,
    caregiverId,
    selectionKey,
}: UseGenerationsPayrollPreviewsArgs): UseGenerationsPayrollPreviewsResult {
    const [state, dispatch] = useReducer(generationsPreviewReducer, {
        phase: 'idle',
        data: EMPTY_PREVIEW_DATA,
    });
    // One active token synchronously prevents overlapping vendor sessions and
    // lets an invalidated serial batch stop after its current request drains.
    const activeRunRef = useRef<ActivePreviewRun | null>(null);

    const resetPreviews = useCallback(() => {
        const activeRun = activeRunRef.current;
        if (activeRun) {
            activeRun.invalidate();
        }
        dispatch({
            type: 'invalidate',
            requestIsDraining: activeRun !== null,
        });
    }, []);

    useEffect(resetPreviews, [
        enabled,
        organizationId,
        outreachId,
        caregiverId,
        selectionKey,
        resetPreviews,
    ]);

    const loadPreviews = useCallback(
        async (shiftIds: ReadonlyArray<string>) => {
            if (
                !enabled ||
                !organizationId ||
                activeRunRef.current !== null ||
                state.phase !== 'idle'
            ) {
                return;
            }
            const run = createActivePreviewRun();
            activeRunRef.current = run;
            dispatch({ type: 'start' });

            const isCurrent = () =>
                activeRunRef.current === run && run.isValid();
            try {
                let payrollShiftIds = shiftIds;
                if (shiftIds.length > 1) {
                    let selection: GenerationsRecurrenceSelectionPreview;
                    try {
                        selection = await loadGenerationsRecurrenceSelection({
                            organizationId,
                            outreachId,
                            caregiverId,
                            shiftIds,
                        });
                    } catch (error) {
                        if (isCurrent()) {
                            dispatch({
                                type: 'error',
                                error: getGenerationsPreviewError(error),
                            });
                        }
                        return;
                    }
                    if (!isCurrent()) {
                        return;
                    }
                    dispatch({ type: 'recurrenceLoaded', selection });
                    payrollShiftIds = selection.assignment_shift_ids;
                    if (payrollShiftIds.length === 0) {
                        dispatch({ type: 'complete', confirmed: true });
                        return;
                    }
                    if (
                        selection.recurrence_applies &&
                        !selection.selection_matches
                    ) {
                        dispatch({ type: 'mismatch' });
                        return;
                    }
                }

                for (const shiftId of payrollShiftIds) {
                    let preview: GenerationsPayrollPreview;
                    try {
                        const response = await getApiClient().post({
                            url: '/organizations/{organization_id}/callouts/{callout_id}/generations-payroll-selection',
                            pathParams: {
                                organization_id: organizationId,
                                callout_id: outreachId,
                            },
                            queryParams: {
                                caregiver_id: caregiverId,
                                shift_id: shiftId,
                            },
                            reason: 'GenerationsPayrollPreview',
                        });
                        if (response.selection_required) {
                            if (!isCurrent()) {
                                return;
                            }
                            // The vendor left the choice to a person. Keep
                            // loading the other visits; this one waits for
                            // a pick, which re-runs its preview alone.
                            dispatch({
                                type: 'optionsLoaded',
                                shiftId,
                                options: response.payroll_options.map(
                                    (option) => ({
                                        payrollItemName:
                                            option.payroll_item_name,
                                        payrollRate: option.payroll_rate,
                                    }),
                                ),
                            });
                            continue;
                        }
                        preview = toPayrollPreview(response);
                    } catch (error) {
                        if (isCurrent()) {
                            dispatch({
                                type: 'error',
                                error: getGenerationsPreviewError(error),
                            });
                        }
                        return;
                    }
                    if (!isCurrent()) {
                        return;
                    }
                    dispatch({ type: 'payrollLoaded', shiftId, preview });
                }
                dispatch({ type: 'complete', confirmed: false });
            } finally {
                if (activeRunRef.current === run) {
                    activeRunRef.current = null;
                    if (!run.isValid()) {
                        dispatch({ type: 'drained' });
                    }
                }
            }
        },
        [enabled, organizationId, outreachId, caregiverId, state.phase],
    );

    const chooseOption = useCallback(
        async (shiftId: string, option: GenerationsPayrollOption) => {
            // Only after a completed load: the batch above owns the vendor
            // session until it settles, and a choice is one more real
            // (non-persisting) postback for exactly one visit.
            if (
                !enabled ||
                !organizationId ||
                activeRunRef.current !== null ||
                state.phase !== 'ready'
            ) {
                return;
            }
            const run = createActivePreviewRun();
            activeRunRef.current = run;
            dispatch({ type: 'start' });
            const isCurrent = () =>
                activeRunRef.current === run && run.isValid();
            try {
                let preview: GenerationsPayrollPreview;
                try {
                    const response = await getApiClient().post({
                        url: '/organizations/{organization_id}/callouts/{callout_id}/generations-payroll-selection',
                        pathParams: {
                            organization_id: organizationId,
                            callout_id: outreachId,
                        },
                        queryParams: {
                            caregiver_id: caregiverId,
                            shift_id: shiftId,
                            payroll_item_name: option.payrollItemName,
                            payroll_rate: option.payrollRate,
                        },
                        reason: 'GenerationsPayrollPreview',
                    });
                    if (response.selection_required) {
                        throw new Error(
                            'Generations did not confirm the chosen pay rate',
                        );
                    }
                    preview = toPayrollPreview(response);
                } catch (error) {
                    if (isCurrent()) {
                        dispatch({
                            type: 'error',
                            error: getGenerationsPreviewError(error),
                        });
                    }
                    return;
                }
                if (!isCurrent()) {
                    return;
                }
                dispatch({ type: 'payrollLoaded', shiftId, preview });
                dispatch({ type: 'complete', confirmed: false });
            } finally {
                if (activeRunRef.current === run) {
                    activeRunRef.current = null;
                    if (!run.isValid()) {
                        dispatch({ type: 'drained' });
                    }
                }
            }
        },
        [enabled, organizationId, outreachId, caregiverId, state.phase],
    );

    return {
        previewsByShiftId: state.data.previewsByShiftId,
        optionsByShiftId: state.data.optionsByShiftId,
        isLoading: state.phase === 'loading',
        loadError: state.phase === 'error' ? state.error.message : null,
        loadErrorCode: state.phase === 'error' ? state.error.code : null,
        isConfirmed: state.data.isConfirmed,
        recurrenceSelection: state.data.recurrenceSelection,
        idempotentShiftIds: state.data.idempotentShiftIds,
        setConfirmed: (confirmed) => dispatch({ type: 'confirm', confirmed }),
        loadPreviews,
        resetPreviews,
        chooseOption,
    };
}

type ConfirmedPayrollSelectionResponse = {
    readonly payroll_item_name: string;
    readonly payroll_rate: string;
    readonly preview_digest: string;
    readonly confirmed_schedule_acknowledgement?: string | null;
    readonly confirmed_schedule_warning?: string | null;
};

function toPayrollPreview(
    response: ConfirmedPayrollSelectionResponse,
): GenerationsPayrollPreview {
    return {
        payrollItemName: response.payroll_item_name,
        payrollRate: response.payroll_rate,
        previewDigest: response.preview_digest,
        confirmedScheduleAcknowledgement:
            response.confirmed_schedule_acknowledgement ?? null,
        confirmedScheduleWarning: response.confirmed_schedule_warning ?? null,
    };
}

type GenerationsShiftPayrollLineProps = {
    readonly preview: GenerationsPayrollPreview | null;
    readonly isLoading: boolean;
    readonly isIdempotent?: boolean;
    // Present while the caregiver's several active pay rates await a choice.
    readonly options?: ReadonlyArray<GenerationsPayrollOption>;
    readonly onChooseOption?: (option: GenerationsPayrollOption) => void;
    readonly disabled?: boolean;
};

/**
 * Per-shift payroll confirmation line. The server returns either the one
 * unambiguous option (vendor-selected, or the sole active one), which renders
 * read-only, or the active options when the vendor left the field blank,
 * which render as a picker whose choice re-runs the preview for that visit.
 */
export function GenerationsShiftPayrollLine({
    preview,
    isLoading,
    isIdempotent = false,
    options,
    onChooseOption,
    disabled = false,
}: GenerationsShiftPayrollLineProps) {
    if (isIdempotent) {
        return (
            <p className="text-muted-foreground mt-2 pl-8 text-xs">
                Already assigned to this caregiver; no Generations write needed
            </p>
        );
    }
    if (preview) {
        return (
            <p className="text-foreground mt-2 pl-8 text-xs">
                Generations payroll: {preview.payrollItemName} — $
                {preview.payrollRate}
            </p>
        );
    }
    if (options !== undefined && options.length > 0) {
        return (
            <div className="mt-2 pl-8">
                <label className="text-foreground block text-xs">
                    <span className="block">
                        Generations offers several pay rates for this caregiver.
                        Choose one:
                    </span>
                    <select
                        aria-label="Generations payroll item"
                        className="border-input bg-background mt-1 h-8 w-full max-w-sm rounded-md border px-2 text-xs"
                        defaultValue=""
                        disabled={disabled || isLoading}
                        onChange={(event) => {
                            const chosen =
                                options[Number(event.currentTarget.value)];
                            if (chosen !== undefined) {
                                onChooseOption?.(chosen);
                            }
                        }}
                    >
                        <option value="" disabled={true}>
                            Choose a pay rate
                        </option>
                        {options.map((option, index) => (
                            <option
                                key={`${option.payrollItemName}-${option.payrollRate}`}
                                value={index}
                            >
                                {option.payrollItemName} — ${option.payrollRate}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
        );
    }
    return (
        <p className="text-muted-foreground mt-2 pl-8 text-xs">
            {isLoading
                ? 'Loading payroll from Generations…'
                : 'Payroll not loaded yet'}
        </p>
    );
}

type GenerationsPayrollSectionProps = {
    readonly selectedShiftCount: number;
    readonly assignmentShiftCount: number;
    readonly loadedShiftCount: number;
    readonly allSelectedShiftsLoaded: boolean;
    // Visits waiting on a pay-rate choice above, after a completed load.
    readonly pendingSelectionCount: number;
    readonly isLoading: boolean;
    readonly disabled: boolean;
    readonly loadError: string | null;
    readonly loadErrorCode: string | null;
    readonly isConfirmed: boolean;
    // The agency's confirmed-visit question, when any loaded visit is one.
    // Null on every ordinary visit, which is the common case: an assignment
    // that touches nothing protected must not grow an extra step.
    readonly confirmedScheduleWarning: string | null;
    readonly recurrenceSelection: GenerationsRecurrenceSelectionPreview | null;
    readonly onLoad: () => void;
    readonly onConfirmedChange: (confirmed: boolean) => void;
    readonly onContinueWithoutWriteback: () => void;
};

/**
 * The explicit load action, batch progress, settled failures, and the
 * deliberate human confirmation for Generations payroll. A failed preview is
 * not automatically retried for the same preview context. The coordinator can
 * inspect Generations and reopen, or explicitly continue with a Phoebe-only
 * assignment.
 */
export function GenerationsPayrollSection({
    selectedShiftCount,
    assignmentShiftCount,
    loadedShiftCount,
    allSelectedShiftsLoaded,
    pendingSelectionCount,
    isLoading,
    disabled,
    loadError,
    loadErrorCode,
    isConfirmed,
    confirmedScheduleWarning,
    recurrenceSelection,
    onLoad,
    onConfirmedChange,
    onContinueWithoutWriteback,
}: GenerationsPayrollSectionProps) {
    return (
        <div className="border-border bg-background mt-3 space-y-3 rounded-lg border p-4">
            <div className="space-y-1">
                <p className="font-medium">Generations payroll</p>
                <p className="text-muted-foreground text-sm">
                    Load the unambiguous payroll item and pay rate Generations
                    shows for this caregiver, then confirm them to assign.
                    Loading does not change anything in Generations.
                </p>
                {selectedShiftCount > 1 && (
                    <p className="text-muted-foreground text-sm">
                        Phoebe will first check which selected visits still need
                        a Generations write. When more than one does, it will
                        verify the complete finite date span and will not change
                        all future visits.
                    </p>
                )}
            </div>
            {recurrenceSelection && !recurrenceSelection.selection_matches ? (
                <Alert variant="destructive">
                    <AlertTitle>
                        Generations would use a different set of visits
                    </AlertTitle>
                    <AlertDescription>
                        <p>
                            Nothing changed. Adjust the selection or assign
                            these visits individually.
                        </p>
                        {recurrenceSelection.extra_computed_occurrences.length >
                            0 && (
                            <div>
                                <p className="font-medium">Also included</p>
                                <GenerationsOccurrenceList
                                    occurrences={
                                        recurrenceSelection.extra_computed_occurrences
                                    }
                                />
                            </div>
                        )}
                        {recurrenceSelection.missing_selected_occurrences
                            .length > 0 && (
                            <div>
                                <p className="font-medium">Not included</p>
                                <GenerationsOccurrenceList
                                    occurrences={
                                        recurrenceSelection.missing_selected_occurrences
                                    }
                                />
                            </div>
                        )}
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onContinueWithoutWriteback}
                            disabled={disabled}
                        >
                            Continue without Generations writeback
                        </Button>
                    </AlertDescription>
                </Alert>
            ) : loadError ? (
                <div
                    className="space-y-1 rounded border border-red-200 bg-red-50 p-3"
                    role="alert"
                >
                    <p className="text-sm font-medium text-red-800">
                        {loadError}
                    </p>
                    {loadErrorCode === GENERATIONS_ASSIGNMENT_CONFLICT_CODE ? (
                        <p className="text-sm text-red-700">
                            Change the selected visits or caregiver before
                            assigning.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            <p className="text-sm text-red-700">
                                {loadErrorCode === GENERATIONS_PORTAL_BUSY_CODE
                                    ? GENERATIONS_PORTAL_BUSY_GUIDANCE_MESSAGE
                                    : GENERATIONS_PREVIEW_GUIDANCE_MESSAGE}
                            </p>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onContinueWithoutWriteback}
                                disabled={disabled}
                            >
                                Continue without Generations writeback
                            </Button>
                        </div>
                    )}
                </div>
            ) : allSelectedShiftsLoaded ? (
                <div className="space-y-3">
                    {recurrenceSelection?.recurrence_applies &&
                        recurrenceSelection.selection_matches && (
                            <div className="space-y-1 rounded border p-3">
                                <p className="text-sm font-medium">
                                    Generations occurrences (
                                    {
                                        recurrenceSelection.computed_occurrences
                                            .length
                                    }
                                    )
                                </p>
                                <GenerationsOccurrenceList
                                    occurrences={
                                        recurrenceSelection.computed_occurrences
                                    }
                                />
                            </div>
                        )}
                    {assignmentShiftCount === 0 ? (
                        <p className="text-muted-foreground text-sm">
                            All selected visits are already assigned to this
                            caregiver. No Generations write is needed.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {confirmedScheduleWarning !== null && (
                                <p
                                    className="border-destructive/40 bg-destructive/10 text-foreground rounded-md border p-3 text-sm"
                                    data-testid="generations-confirmed-schedule-warning"
                                >
                                    {confirmedScheduleWarning}
                                </p>
                            )}
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="confirmGenerationsPayroll"
                                    checked={isConfirmed}
                                    onCheckedChange={(checked) =>
                                        onConfirmedChange(checked === true)
                                    }
                                    disabled={disabled}
                                />
                                <label
                                    htmlFor="confirmGenerationsPayroll"
                                    className="cursor-pointer text-sm font-medium"
                                >
                                    I confirm the Generations{' '}
                                    {recurrenceSelection?.recurrence_applies
                                        ? 'occurrences, payroll items, and rates'
                                        : 'payroll item and rate'}{' '}
                                    shown above
                                    {confirmedScheduleWarning !== null
                                        ? ', and the change to this confirmed visit'
                                        : ''}
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            ) : pendingSelectionCount > 0 && !isLoading ? (
                <p className="text-muted-foreground text-sm">
                    Choose a pay rate for{' '}
                    {pendingSelectionCount === 1
                        ? 'the visit'
                        : `${pendingSelectionCount} visits`}{' '}
                    above to continue.
                </p>
            ) : (
                <div className="flex items-center gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onLoad}
                        disabled={
                            disabled || isLoading || selectedShiftCount === 0
                        }
                    >
                        Load payroll from Generations
                    </Button>
                    {isLoading ? (
                        <p
                            role="status"
                            className="text-muted-foreground flex items-center gap-2 text-sm"
                        >
                            <Spinner size="md" />
                            Loading from Generations ({loadedShiftCount}/
                            {selectedShiftCount})…
                        </p>
                    ) : null}
                </div>
            )}
        </div>
    );
}
